import type {
  AppointmentPlanningPreviewResponse,
  AppointmentPlanningPreviewRow,
} from "../../../../../../../../../../../../../api/kehrblatt/planning"
import type { PlanAppointmentPlanningPreview } from "../contract"

const INBOX_PREVIEW_ROW_ERROR = "INBOX_PREVIEW_ROW_ERROR"

const duplicateValues = (values: string[]): string[] => {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  for (const value of values) {
    if (seen.has(value)) duplicates.add(value)

    seen.add(value)
  }

  return Array.from(duplicates)
}

const errorRow = (actionCaseId: string, message: string): AppointmentPlanningPreviewRow => ({
  client_row_id: actionCaseId,
  status: "unplanned",
  appointment: null,
  warnings: [
    {
      code: INBOX_PREVIEW_ROW_ERROR,
      severity: "error",
      message,
    },
  ],
})

const summaryForRows = (
  rows: AppointmentPlanningPreviewRow[],
): AppointmentPlanningPreviewResponse["summary"] => ({
  total_rows: rows.length,
  planned_rows: rows.filter((row) => row.status === "planned").length,
  unplanned_rows: rows.filter((row) => row.status === "unplanned").length,
  warning_rows: rows.filter((row) => row.warnings.length > 0).length,
  error_rows: rows.filter((row) => row.warnings.some((warning) => warning.severity === "error"))
    .length,
})

export const buildPlanningResponseForSurface = ({
  preview,
  requestedActionCaseIds,
}: {
  preview: PlanAppointmentPlanningPreview
  requestedActionCaseIds: string[]
}): AppointmentPlanningPreviewResponse => {
  const duplicateRequestedIds = duplicateValues(requestedActionCaseIds)

  if (duplicateRequestedIds.length) {
    throw new Error(
      `Terminplanungs-Vorschau enthält doppelte angefragte Zeilen: ${duplicateRequestedIds.join(", ")}.`,
    )
  }

  const rowsById = new Map<string, AppointmentPlanningPreviewRow>()
  for (const row of preview.planning.rows) {
    if (rowsById.has(row.client_row_id)) {
      throw new Error(`Terminplanungs-Vorschau enthält die Zeile ${row.client_row_id} mehrfach.`)
    }

    rowsById.set(row.client_row_id, row)
  }

  const requestedIds = new Set(requestedActionCaseIds)
  const unexpectedIds = Array.from(
    new Set([...rowsById.keys(), ...Object.keys(preview.rowErrorsByActionCaseId)]),
  ).filter((actionCaseId) => !requestedIds.has(actionCaseId))

  if (unexpectedIds.length) {
    throw new Error(
      `Terminplanungs-Vorschau enthält nicht angefragte Zeilen: ${unexpectedIds.join(", ")}.`,
    )
  }

  const rows = requestedActionCaseIds.map((actionCaseId) => {
    const row = rowsById.get(actionCaseId)
    const rowError = preview.rowErrorsByActionCaseId[actionCaseId]

    if (row && rowError) {
      throw new Error(
        `Terminplanungs-Vorschau enthält für ${actionCaseId} gleichzeitig Ergebnis und Fehler.`,
      )
    }

    if (row) return row

    if (rowError) return errorRow(actionCaseId, rowError)

    throw new Error(`Terminplanungs-Vorschau enthält kein Ergebnis für ${actionCaseId}.`)
  })

  if (!Object.keys(preview.rowErrorsByActionCaseId).length) return preview.planning

  return {
    ...preview.planning,
    rows,
    summary: summaryForRows(rows),
  }
}
